import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn, getBadgeColor, getButtonColor } from '@/lib/utils';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import {
  ArrowLeft,
  TrendingUp,
  Clock,
  AlertTriangle,
  Award,
  BookOpen,
  Users,
  Target,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Zap,
  Lightbulb,
  CheckCircle,
  Eye
} from 'lucide-react';

export const TeacherStudentProfile = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { teacherTheme, currentClass } = useTeacher();
  const isDark = teacherTheme === 'dark';
  const [studentData, setStudentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Synced student data (4 students with matching IDs from Student Insights and Curiosity Analytics)
  const studentGroups = {
    'Physics - Grade 7A': [
      { id: 1, name: 'Anjali Patel', class: 'Physics - Grade 7A', avgScore: 88 }, // Good (80+)
      { id: 2, name: 'Arjun Sharma', class: 'Physics - Grade 7A', avgScore: 45 }  // Needs Help (<60)
    ],
    'Physics - Grade 8A': [
      { id: 4, name: 'Shreya Patel', class: 'Physics - Grade 8A', avgScore: 48 },    // Needs Help (<60)
      { id: 6, name: 'Siddharth Joshi', class: 'Physics - Grade 8A', avgScore: 42 } // Needs Help (<60)
    ]
  };

  // Curiosity Analytics data for the 4 students
  const curiosityAnalyticsData = {
    1: { totalInteractions: 55, timeSpentMinutes: 150, coreInteractions: 32, deepInteractions: 23, completionRate: 91.2, chaptersEngaged: 11, usageLevel: 'high' },   // Anjali - High
    2: { totalInteractions: 22, timeSpentMinutes: 55, coreInteractions: 18, deepInteractions: 4, completionRate: 65.2, chaptersEngaged: 5, usageLevel: 'medium' },  // Arjun - Medium
    4: { totalInteractions: 16, timeSpentMinutes: 40, coreInteractions: 12, deepInteractions: 4, completionRate: 59.2, chaptersEngaged: 3, usageLevel: 'low' },   // Shreya - Low
    6: { totalInteractions: 12, timeSpentMinutes: 30, coreInteractions: 10, deepInteractions: 2, completionRate: 54.8, chaptersEngaged: 2, usageLevel: 'low' }   // Siddharth - Low
  };

  // Subject-wise performance data
  const subjectPerformanceData = {
    1: [
      { subject: 'Physics', score: 88, trend: 'up' },
      { subject: 'Mathematics', score: 85, trend: 'stable' },
      { subject: 'Chemistry', score: 90, trend: 'up' }
    ],
    2: [
      { subject: 'Physics', score: 45, trend: 'down' },
      { subject: 'Mathematics', score: 50, trend: 'stable' },
      { subject: 'Chemistry', score: 40, trend: 'down' }
    ],
    3: [
      { subject: 'Physics', score: 88, trend: 'up' },
      { subject: 'Mathematics', score: 82, trend: 'stable' },
      { subject: 'Chemistry', score: 94, trend: 'up' }
    ],
    4: [
      { subject: 'Physics', score: 48, trend: 'stable' },
      { subject: 'Mathematics', score: 52, trend: 'up' },
      { subject: 'Chemistry', score: 44, trend: 'stable' }
    ],
    5: [
      { subject: 'Physics', score: 95, trend: 'up' },
      { subject: 'Mathematics', score: 92, trend: 'up' },
      { subject: 'Chemistry', score: 98, trend: 'up' }
    ],
    6: [
      { subject: 'Physics', score: 42, trend: 'down' },
      { subject: 'Mathematics', score: 45, trend: 'stable' },
      { subject: 'Chemistry', score: 40, trend: 'down' }
    ],
    7: [
      { subject: 'Physics', score: 90, trend: 'stable' },
      { subject: 'Mathematics', score: 88, trend: 'up' },
      { subject: 'Chemistry', score: 92, trend: 'stable' }
    ],
    8: [
      { subject: 'Physics', score: 52, trend: 'up' },
      { subject: 'Mathematics', score: 55, trend: 'up' },
      { subject: 'Chemistry', score: 50, trend: 'stable' }
    ]
  };

  // Chapter breakdown data
  const chapterBreakdownData = {
    1: [
      { chapter: 'Light and Reflection', interactions: 20, core: 15, deep: 5, completion: 85 },
      { chapter: 'Motion and Forces', interactions: 15, core: 10, deep: 5, completion: 75 },
      { chapter: 'Sound Waves', interactions: 10, core: 5, deep: 5, completion: 75 }
    ],
    2: [
      { chapter: 'Light and Reflection', interactions: 12, core: 10, deep: 2, completion: 70 },
      { chapter: 'Motion and Forces', interactions: 8, core: 6, deep: 2, completion: 60 },
      { chapter: 'Sound Waves', interactions: 2, core: 2, deep: 0, completion: 60 }
    ],
    3: [
      { chapter: 'Light and Reflection', interactions: 22, core: 14, deep: 8, completion: 90 },
      { chapter: 'Motion and Forces', interactions: 16, core: 10, deep: 6, completion: 85 },
      { chapter: 'Sound Waves', interactions: 10, core: 6, deep: 4, completion: 80 }
    ],
    4: [
      { chapter: 'Light and Reflection', interactions: 8, core: 6, deep: 2, completion: 65 },
      { chapter: 'Motion and Forces', interactions: 6, core: 4, deep: 2, completion: 55 },
      { chapter: 'Sound Waves', interactions: 2, core: 2, deep: 0, completion: 55 }
    ],
    5: [
      { chapter: 'Light and Reflection', interactions: 25, core: 15, deep: 10, completion: 95 },
      { chapter: 'Motion and Forces', interactions: 20, core: 12, deep: 8, completion: 90 },
      { chapter: 'Sound Waves', interactions: 10, core: 5, deep: 5, completion: 85 }
    ],
    6: [
      { chapter: 'Light and Reflection', interactions: 6, core: 5, deep: 1, completion: 60 },
      { chapter: 'Motion and Forces', interactions: 4, core: 3, deep: 1, completion: 50 },
      { chapter: 'Sound Waves', interactions: 2, core: 2, deep: 0, completion: 55 }
    ],
    7: [
      { chapter: 'Light and Reflection', interactions: 22, core: 14, deep: 8, completion: 90 },
      { chapter: 'Motion and Forces', interactions: 15, core: 10, deep: 5, completion: 85 },
      { chapter: 'Sound Waves', interactions: 9, core: 5, deep: 4, completion: 80 }
    ],
    8: [
      { chapter: 'Light and Reflection', interactions: 7, core: 5, deep: 2, completion: 65 },
      { chapter: 'Motion and Forces', interactions: 5, core: 4, deep: 1, completion: 55 },
      { chapter: 'Sound Waves', interactions: 2, core: 2, deep: 0, completion: 50 }
    ]
  };

  const getAllStudents = () => Object.values(studentGroups).flat();
  const allStudents = getAllStudents();
  const studentIdNum = studentId ? parseInt(studentId, 10) : null;

  useEffect(() => {
    const loadStudent = async () => {
      const token = getAuthToken('teacher');
      if (!token || !studentIdNum) {
        setIsLoading(false);
        return;
      }
      try {
        const params = currentClass?.id ? `?class_id=${currentClass.id}` : '';
        const res = await axios.get(apiUrl(`v1/teachers/students/${studentIdNum}${params}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStudentData(res.data);
      } catch (e) {
        console.error('Failed to load student profile', e);
      } finally {
        setIsLoading(false);
      }
    };
    loadStudent();
  }, [studentIdNum, currentClass?.id]);

  const apiStudent = studentData
    ? {
        id: studentIdNum,
        name: studentData.name,
        class: studentData.class,
        avgScore: studentData.avg_score || 0,
        email: studentData.email || '',
        rollNumber: studentData.roll_number || '',
        joinDate: studentData.join_date || ''
      }
    : null;

  const student = apiStudent || (studentIdNum !== null && !isNaN(studentIdNum)
    ? allStudents.find(s => s.id === studentIdNum)
    : null);

  const studentCuriosity = studentData?.curiosity_analytics || curiosityAnalyticsData[student?.id] || {};
  const studentSubjects = (studentData?.subject_performance || []).map((s) => ({
    subject: s.subject,
    score: s.score,
    trend: s.trend || 'stable'
  })) || subjectPerformanceData[student?.id] || [];
  const studentChapters = chapterBreakdownData[student?.id] || [];

  // Get performance level based on score
  const getPerformanceLevel = (score) => {
    if (score >= 80) return { label: 'Good', class: 'bg-green-500 text-white' };
    if (score >= 60) return { label: 'Fair', class: 'bg-yellow-500 text-white' };
    return { label: 'Needs Help', class: 'bg-red-500 text-white' };
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />;
    return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
  };

  // Get usage level color
  const getUsageLevelColor = (level) => {
    if (level === 'high') return 'bg-green-500';
    if (level === 'medium') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center h-screen", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
        <div className="text-center">
          <h1 className={cn("text-2xl font-bold mb-4", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Loading student...</h1>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className={cn("flex items-center justify-center h-screen", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
        <div className="text-center">
          <h1 className={cn("text-2xl font-bold mb-4", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Student Not Found</h1>
          <Button 
            onClick={() => navigate(-1)} 
            className={cn("mt-4", getButtonColor(isDark))}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  const performanceLevel = getPerformanceLevel(student.avgScore);
  const curiosityLevel = getUsageLevelColor(studentCuriosity.usageLevel || 'low');

  return (
    <div className={cn("flex flex-col h-full p-6 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/60" : "text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EBE3]/60")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{student.name}</h1>
            <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Student Profile & Performance Analysis</p>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Performance Overview */}
          <div className="space-y-6">
            {/* Performance Overview Card */}
            <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
              <CardHeader className={cn("border-b", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
                  <Award className="w-5 h-5" />
                  Performance Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Overall Performance */}
                <div className="space-y-4">
                  <h3 className={cn("text-lg font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Overall Performance</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                      <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Average Score</p>
                      <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")}>{student.avgScore}%</p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                      <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Performance Level</p>
                      <span className={cn("text-sm font-semibold px-2 py-1 rounded", performanceLevel.class)}>
                        {performanceLevel.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Subject-wise Performance */}
                <div className="space-y-4">
                  <h3 className={cn("text-lg font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Subject-wise Performance</h3>
                  
                  <div className="space-y-4">
                    {studentSubjects.map((subject, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <p className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{subject.subject}</p>
                          <div className="flex items-center gap-2">
                            {getTrendIcon(subject.trend)}
                            <span className={cn("font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{subject.score}%</span>
                          </div>
                        </div>
                        <Progress value={subject.score} className="h-2" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Student Information */}
                <div className="space-y-4">
                  <h3 className={cn("text-lg font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Student Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Email</p>
                      <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
                        {student?.email || ''}
                      </p>
                    </div>
                    <div>
                      <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Roll Number</p>
                      <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
                        {student?.rollNumber || ''}
                      </p>
                    </div>
                    <div>
                      <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Class</p>
                      <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{student?.class || ''}</p>
                    </div>
                    <div>
                      <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Join Date</p>
                      <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
                        {student?.joinDate 
                          ? new Date(student.joinDate).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })
                          : ''}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Curiosity Analytics */}
          <div className="space-y-6">
            {/* Curiosity Analytics Card */}
            <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
              <CardHeader className={cn("border-b", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
                  <Lightbulb className="w-5 h-5" />
                  Curiosity Analytics
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Key Metrics */}
                <div className="space-y-4">
                  <h3 className={cn("text-lg font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Key Metrics</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                      <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Total Interactions</p>
                      <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")}>{studentCuriosity.totalInteractions || 0}</p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                      <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Time Spent</p>
                      <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")}>{studentCuriosity.timeSpentMinutes || 0}m</p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                      <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Completion Rate</p>
                      <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")}>{studentCuriosity.completionRate ? studentCuriosity.completionRate.toFixed(1) : '0.0'}%</p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                      <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Usage Level</p>
                      <span className={cn("text-sm font-semibold px-2 py-1 rounded text-white", curiosityLevel)}>
                        {(studentCuriosity.usageLevel || 'low').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content Usage */}
                <div className="space-y-4">
                  <h3 className={cn("text-lg font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Content Usage</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-blue-500" />
                        <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Core Content</p>
                      </div>
                      <p className={cn("text-xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{studentCuriosity.coreInteractions || 0}</p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}>
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-purple-500" />
                        <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Deep Content</p>
                      </div>
                      <p className={cn("text-xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{studentCuriosity.deepInteractions || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Chapter Breakdown */}
                <div className="space-y-4">
                  <h3 className={cn("text-lg font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Chapter Breakdown</h3>
                  
                  <div className="space-y-3">
                    {studentChapters.map((chapter, index) => (
                      <div 
                        key={index} 
                        className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F5F1EA] border-[#E0D4BF]")}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <p className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{chapter.chapter}</p>
                          <span className={cn("text-sm font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{chapter.interactions} interactions</span>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <div className="text-center">
                            <p className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Core</p>
                            <p className={cn("font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{chapter.core}</p>
                          </div>
                          <div className="text-center">
                            <p className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Deep</p>
                            <p className={cn("font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{chapter.deep}</p>
                          </div>
                          <div className="text-center">
                            <p className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Completion</p>
                            <p className={cn("font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{chapter.completion}%</p>
                          </div>
                        </div>
                        
                        <Progress value={chapter.completion} className="h-1.5" />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 flex-wrap">
          <Button className={cn(getButtonColor(isDark))}>
            <Mail className="w-4 h-4 mr-2" />
            Send Message
          </Button>
          <Button className={cn(isDark ? "bg-[hsl(var(--card-border))] hover:bg-[hsl(var(--card-border))]/80 text-[hsl(var(--foreground))]" : "bg-[#E0D4BF] hover:bg-[#D4C4A8] text-[#1A1A1A]")}>
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Session
          </Button>
          <Button className={cn(isDark ? "bg-[hsl(var(--card-border))] hover:bg-[hsl(var(--card-border))]/80 text-[hsl(var(--foreground))]" : "bg-[#E0D4BF] hover:bg-[#D4C4A8] text-[#1A1A1A]")}>
            <Eye className="w-4 h-4 mr-2" />
            View Full Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeacherStudentProfile;
